import React, { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

const THRESHOLD = 60;

function formatDollars(amount) {
  const n = typeof amount === 'number' ? amount : parseFloat(amount);
  if (Number.isNaN(n)) return '0.00';
  return n.toFixed(2);
}

export default function FreeShippingProgress({
  subtotal,
  threshold = THRESHOLD,
  tone = 'dark',
  showSubtotal = true,
}) {
  const numericSubtotal =
    typeof subtotal === 'number' ? subtotal : parseFloat(subtotal);
  const safeSubtotal = Number.isNaN(numericSubtotal) ? 0 : numericSubtotal;
  const remaining = Math.max(0, threshold - safeSubtotal);
  const unlocked = remaining <= 0;

  const progress = useMemo(() => {
    if (threshold <= 0) return 100;
    return Math.max(0, Math.min(100, (safeSubtotal / threshold) * 100));
  }, [safeSubtotal, threshold]);

  const colors =
    tone === 'dark'
      ? {
          text: '#fff9d4',
          track: 'rgba(255, 249, 212, 0.18)',
          bar: '#fff9d4',
          subtle: 'rgba(255, 249, 212, 0.75)',
          border: 'rgba(255, 249, 212, 0.18)',
        }
      : {
          text: '#1a1a1a',
          track: 'rgba(0, 0, 0, 0.10)',
          bar: '#1a1a1a',
          subtle: 'rgba(0, 0, 0, 0.65)',
          border: 'rgba(0, 0, 0, 0.12)',
        };

  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.25,
        py: 1,
        borderRadius: '10px',
        border: `1px solid ${colors.border}`,
        background: 'rgba(255,255,255,0.02)',
      }}
      role="status"
      aria-label="Free shipping progress"
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
        <Typography sx={{ color: colors.text, fontWeight: 600 }}>
          Free shipping at ${threshold}
        </Typography>
        {showSubtotal && (
          <Typography sx={{ color: colors.subtle, fontWeight: 600 }}>
            Subtotal ${formatDollars(safeSubtotal)}
          </Typography>
        )}
      </Box>

      <Box sx={{ mt: 0.75 }}>
        <LinearProgress
          variant="determinate"
          value={unlocked ? 100 : progress}
          sx={{
            height: 8,
            borderRadius: 99,
            backgroundColor: colors.track,
            '& .MuiLinearProgress-bar': {
              borderRadius: 99,
              backgroundColor: colors.bar,
            },
          }}
        />
      </Box>

      <Typography sx={{ mt: 0.75, color: colors.text }}>
        {unlocked
          ? 'Free shipping unlocked'
          : `Spend $${formatDollars(remaining)} more to unlock free shipping`}
      </Typography>
    </Box>
  );
}
